/**
 * WebSocket Service for Real-Time Messaging
 * 
 * Handles WebSocket connections, automatic reconnection,
 * message queuing, and event management.
 */

// Simple EventEmitter for browser
class EventEmitter {
  private events: Map<string, Function[]> = new Map();

  on(event: string, listener: Function): void {
    if (!this.events.has(event)) {
      this.events.set(event, []);
    }
    this.events.get(event)!.push(listener);
  }

  off(event: string, listener: Function): void {
    const listeners = this.events.get(event);
    if (listeners) {
      const index = listeners.indexOf(listener);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  emit(event: string, ...args: any[]): void {
    const listeners = this.events.get(event);
    if (listeners) {
      listeners.forEach(listener => listener(...args));
    }
  }

  removeAllListeners(): void {
    this.events.clear();
  }
}

// WebSocket message types matching backend
export interface WebSocketMessage {
  type: string;
  [key: string]: any;
}

export interface SendMessagePayload {
  content: string;
  conversation_id?: string;
}

export interface UploadFilePayload {
  filename: string;
  mime_type: string;
  data: string; // base64
  conversation_id?: string;
}

export interface TypingPayload {
  conversation_id: string;
}

export interface MarkReadPayload {
  message_id: string;
}

export type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'reconnecting' | 'error';

export interface WebSocketServiceConfig {
  url: string;
  token: string;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  heartbeatInterval?: number;
}

export class WebSocketService extends EventEmitter {
  private ws: WebSocket | null = null;
  private config: WebSocketServiceConfig;
  private reconnectAttempts = 0;
  private reconnectTimer: number | null = null;
  private heartbeatTimer: number | null = null;
  private messageQueue: WebSocketMessage[] = [];
  private connectionState: ConnectionState = 'disconnected';
  private isIntentionallyClosed = false;

  constructor(config: WebSocketServiceConfig) {
    super();
    this.config = {
      reconnectInterval: 3000,
      maxReconnectAttempts: 10,
      heartbeatInterval: 30000,
      ...config,
    };
  }

  /**
   * Connect to WebSocket server
   */
  public connect(): void {
    if (this.ws && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN)) {
      console.log('[WebSocket] Already connected or connecting');
      return;
    }

    this.isIntentionallyClosed = false;
    this.setConnectionState('connecting');

    try {
      const wsUrl = `${this.config.url}?token=${encodeURIComponent(this.config.token)}`;
      console.log('[WebSocket] Connecting to:', wsUrl.replace(this.config.token, '***'));
      
      this.ws = new WebSocket(wsUrl);
      this.setupEventHandlers();
    } catch (error) {
      console.error('[WebSocket] Connection error:', error);
      this.setConnectionState('error');
      this.handleReconnect();
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  public disconnect(): void {
    console.log('[WebSocket] Disconnecting...');
    this.isIntentionallyClosed = true;
    this.clearTimers();
    
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    
    this.setConnectionState('disconnected');
    this.messageQueue = [];
  }

  /**
   * Send a message
   */
  public sendMessage(payload: SendMessagePayload): void {
    this.send({
      type: 'send_message',
      ...payload,
    });
  }

  /**
   * Upload a file
   */
  public uploadFile(payload: UploadFilePayload): void {
    this.send({
      type: 'upload_file',
      ...payload,
    });
  }

  /**
   * Send typing start indicator
   */
  public sendTypingStart(conversationId: string): void {
    this.send({
      type: 'typing_start',
      conversation_id: conversationId,
    });
  }

  /**
   * Send typing stop indicator
   */
  public sendTypingStop(conversationId: string): void {
    this.send({
      type: 'typing_stop',
      conversation_id: conversationId,
    });
  }

  /**
   * Mark message as read
   */
  public markRead(messageId: string): void {
    this.send({
      type: 'mark_read',
      message_id: messageId,
    });
  }

  /**
   * Send ping
   */
  public ping(): void {
    this.send({ type: 'ping' });
  }

  /**
   * Get current connection state
   */
  public getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  /**
   * Check if connected
   */
  public isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Send a message through WebSocket
   */
  private send(message: WebSocketMessage): void {
    if (!this.isConnected()) {
      console.log('[WebSocket] Not connected, queuing message:', message.type);
      this.messageQueue.push(message);
      return;
    }

    try {
      const payload = JSON.stringify(message);
      this.ws!.send(payload);
      console.log('[WebSocket] Sent:', message.type);
    } catch (error) {
      console.error('[WebSocket] Send error:', error);
      this.messageQueue.push(message);
    }
  }

  /**
   * Setup WebSocket event handlers
   */
  private setupEventHandlers(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      console.log('[WebSocket] Connected');
      this.setConnectionState('connected');
      this.reconnectAttempts = 0;
      this.startHeartbeat();
      this.flushMessageQueue();
      this.emit('connected');
    };

    this.ws.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        console.log('[WebSocket] Received:', message.type);
        this.handleMessage(message);
      } catch (error) {
        console.error('[WebSocket] Parse error:', error);
      }
    };

    this.ws.onerror = (error) => {
      console.error('[WebSocket] Error:', error);
      this.setConnectionState('error');
      this.emit('error', error);
    };

    this.ws.onclose = (event) => {
      console.log('[WebSocket] Closed:', event.code, event.reason);
      this.clearTimers();
      this.setConnectionState('disconnected');
      this.emit('disconnected', { code: event.code, reason: event.reason });

      if (!this.isIntentionallyClosed) {
        this.handleReconnect();
      }
    };
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(message: WebSocketMessage): void {
    switch (message.type) {
      case 'connected':
        this.emit('connected', message);
        break;

      case 'message_received':
        this.emit('message', message.message);
        break;

      case 'message_delivered':
        this.emit('message_delivered', {
          message_id: message.message_id,
          delivered_at: message.delivered_at,
        });
        break;

      case 'typing_indicator':
        this.emit('typing', {
          user_id: message.user_id,
          user_name: message.user_name,
          is_typing: message.is_typing,
          conversation_id: message.conversation_id,
        });
        break;

      case 'read_receipt':
        this.emit('read_receipt', {
          message_id: message.message_id,
          read_by: message.read_by,
          read_at: message.read_at,
        });
        break;

      case 'presence_update':
        this.emit('presence', {
          user_id: message.user_id,
          status: message.status,
        });
        break;

      case 'file_upload_progress':
        this.emit('upload_progress', {
          upload_id: message.upload_id,
          progress: message.progress,
        });
        break;

      case 'file_upload_complete':
        this.emit('upload_complete', {
          upload_id: message.upload_id,
          file_url: message.file_url,
          attachment: message.attachment,
        });
        break;

      case 'missed_messages':
        this.emit('missed_messages', {
          messages: message.messages,
          count: message.count,
        });
        break;

      case 'error':
        this.emit('server_error', {
          code: message.code,
          message: message.message,
        });
        break;

      case 'pong':
        // Heartbeat response
        break;

      default:
        console.warn('[WebSocket] Unknown message type:', message.type);
    }
  }

  /**
   * Handle reconnection logic
   */
  private handleReconnect(): void {
    if (this.isIntentionallyClosed) {
      return;
    }

    if (this.reconnectAttempts >= this.config.maxReconnectAttempts!) {
      console.error('[WebSocket] Max reconnect attempts reached');
      this.setConnectionState('error');
      this.emit('max_reconnect_attempts');
      return;
    }

    this.reconnectAttempts++;
    this.setConnectionState('reconnecting');
    
    console.log(
      `[WebSocket] Reconnecting... (attempt ${this.reconnectAttempts}/${this.config.maxReconnectAttempts})`
    );

    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, this.config.reconnectInterval);
  }

  /**
   * Start heartbeat to keep connection alive
   */
  private startHeartbeat(): void {
    this.clearHeartbeat();
    
    this.heartbeatTimer = setInterval(() => {
      if (this.isConnected()) {
        this.ping();
      }
    }, this.config.heartbeatInterval);
  }

  /**
   * Clear heartbeat timer
   */
  private clearHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * Clear all timers
   */
  private clearTimers(): void {
    this.clearHeartbeat();
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * Flush queued messages
   */
  private flushMessageQueue(): void {
    if (this.messageQueue.length === 0) return;

    console.log(`[WebSocket] Flushing ${this.messageQueue.length} queued messages`);
    
    const queue = [...this.messageQueue];
    this.messageQueue = [];
    
    queue.forEach((message) => {
      this.send(message);
    });
  }

  /**
   * Set connection state and emit event
   */
  private setConnectionState(state: ConnectionState): void {
    if (this.connectionState !== state) {
      this.connectionState = state;
      this.emit('state_change', state);
      console.log('[WebSocket] State changed to:', state);
    }
  }
}

// Singleton instance
let wsInstance: WebSocketService | null = null;

/**
 * Get or create WebSocket service instance
 */
export function getWebSocketService(config?: WebSocketServiceConfig): WebSocketService {
  if (!wsInstance && config) {
    wsInstance = new WebSocketService(config);
  }
  
  if (!wsInstance) {
    throw new Error('WebSocket service not initialized. Call with config first.');
  }
  
  return wsInstance;
}

/**
 * Destroy WebSocket service instance
 */
export function destroyWebSocketService(): void {
  if (wsInstance) {
    wsInstance.disconnect();
    wsInstance.removeAllListeners();
    wsInstance = null;
  }
}
